import { useEffect, useMemo, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, ChevronDown, Clock, CreditCard, Coins, DollarSign, FileText, ListTodo, Loader2, PhoneCall, ShieldCheck, ShoppingBag, Sparkles, Timer, ZapOff } from 'lucide-react';
import type { CrmRequiredActionItem, CrmRequiredActionsGrouped } from '../../lib/crm/requiredActions';
import LeadScoreThermometer from '../crm/LeadScoreThermometer';
import {
  flattenRequiredActions,
  getNewLeadResponseSeverity,
  sortRequiredActionsNewestFirst,
  type NewLeadResponseSeverity,
} from '../../lib/crm/requiredActions';
import { formatCurrency } from '../../lib/utils';
import { hapticMedium } from '../../lib/haptics';
import { useLongPress } from '../../hooks/useLongPress';
import type { QuickActionsAnchor } from './CrmLeadQuickActionsMenu';
import SalesPlayCard from './SalesPlayCard';
import { SalesFace } from '../NexusFace';
import type { SalesPlay } from '../../hooks/useSalesPlays';

interface CrmActionsRequiredPanelProps {
  actions: CrmRequiredActionsGrouped;
  loading?: boolean;
  /** 'hero' renders the control-centre hero look: white card with a
   *  "Needs your attention" header, overdue/waiting legend + grouped sections. */
  variant?: 'light' | 'hero';
  onSelectOpportunity?: (opportunityId: number) => void;
  onSelectProposal?: (proposalId: string) => void;
  onSelectPayrollRequest?: (payrollRequestId: string) => void;
  onSelectPayrollPeriod?: (periodStart: string, periodEnd: string) => void;
  onSelectDocumentVerification?: (submissionId: string) => void;
  onSelectCampaignBilling?: (campaignId: string) => void;
  onSelectMarketingAiTokens?: () => void;
  onSelectCallback?: (item: CrmRequiredActionItem) => void;
  onSelectDueTask?: (item: CrmRequiredActionItem) => void;
  onSelectOrder?: (item: CrmRequiredActionItem) => void;
  /** Press-and-hold on a lead row opens quick actions anchored to it. */
  onLeadQuickActions?: (item: CrmRequiredActionItem, anchor: QuickActionsAnchor) => void;
  /** Fill parent height (control-centre sidebar) instead of fixed min(480px, 60vh). */
  fillHeight?: boolean;
  /** Mobile only: render the header as a tappable dropdown that collapses the list. */
  collapsible?: boolean;
  /** When provided, renders an AI "Summary" button in the header. */
  onRequestSummary?: () => void;
  /** Moves the Sales manager has staged, by lead; each shows under its lead's row with a Send button. */
  salesPlays?: Map<number, SalesPlay>;
  onSalesPlayActed?: (playId: string, status: 'sent' | 'done' | 'skipped') => void;
  /** The send finished in the background. */
  onSalesPlaySettled?: (playId: string) => void;
  /** The send failed. The card comes back with this message. */
  onSalesPlayFailed?: (playId: string, message: string) => void;
  className?: string;
}

/** Items whose deadline has already passed ("Overdue" section); the rest are
 *  requests waiting on the viewer ("Waiting on you"). */
function isOverdueItem(item: CrmRequiredActionItem, now: Date): boolean {
  switch (item.kind) {
    case 'follow_up_due':
    case 'no_activity':
    case 'agent_callback':
      return true;
    case 'due_task':
      return item.todoOverdue === true;
    case 'payroll_period':
      return (item.payrollPeriodDaysPastDue ?? 0) > 0;
    case 'new_lead':
      return getNewLeadResponseSeverity(item.createdAt, now) !== 'none';
    default:
      return false;
  }
}

const NEW_LEAD_SEVERITY_STYLES: Record<
  Exclude<NewLeadResponseSeverity, 'none'>,
  { icon: string; label: string }
> = {
  warning: {
    icon: 'text-amber-500',
    label: 'Lead waiting 2–4 hours — respond soon',
  },
  critical: {
    icon: 'text-red-600',
    label: 'Lead waiting over 4 hours — respond urgently',
  },
};

function NewLeadSeverityBadge({ severity }: { severity: NewLeadResponseSeverity }) {
  if (severity === 'none') return null;

  const { icon, label } = NEW_LEAD_SEVERITY_STYLES[severity];
  return (
    <span
      className="mt-0.5 shrink-0 flex items-center"
      title={label}
      aria-label={label}
    >
      <AlertTriangle
        className={`h-4 w-4 fill-current ${icon}`}
        aria-hidden
      />
    </span>
  );
}

function ActionRow({
  icon,
  iconClassName,
  categoryLabel,
  categoryClassName,
  title,
  meta,
  leadScore,
  responseSeverity,
  onClick,
  onQuickActions,
}: {
  icon: React.ReactNode;
  iconClassName: string;
  /** Top line — e.g. "Lead · Follow up". Sets the action's category so it's clear at a glance which app the action belongs to. */
  categoryLabel: string;
  /** Accent colour for the category line (text-{tone}-700). */
  categoryClassName: string;
  /** Middle line — the main thing being acted on (opportunity title, proposal title, employee name, …). */
  title: string;
  /** Bottom line — combined customer + status (e.g. "John Doe · Due 5 hours ago"). */
  meta: string;
  /** Sales' read of this lead. Hidden until set. */
  leadScore?: number | null;
  responseSeverity?: NewLeadResponseSeverity;
  onClick: () => void;
  /** When set, press-and-hold (or right-click) opens quick actions for this row. */
  onQuickActions?: (anchor: QuickActionsAnchor) => void;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  // A long press is followed by a native click — swallow that one so the row
  // doesn't also open the full detail view behind the menu.
  const suppressClickRef = useRef(false);

  const openQuickActions = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect || !onQuickActions) return;
    void hapticMedium();
    onQuickActions({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    });
  };

  const longPressHandlers = useLongPress({
    onLongPress: () => {
      if (!onQuickActions) return;
      suppressClickRef.current = true;
      openQuickActions();
    },
    threshold: 500,
  });

  const pressHandlers = onQuickActions ? longPressHandlers : {};

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={() => {
        if (suppressClickRef.current) {
          suppressClickRef.current = false;
          return;
        }
        onClick();
      }}
      onContextMenu={
        onQuickActions
          ? (e) => {
              e.preventDefault();
              suppressClickRef.current = true;
              openQuickActions();
            }
          : undefined
      }
      {...pressHandlers}
      className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-gray-50 active:bg-gray-100 touch-manipulation sm:items-start sm:gap-2.5 sm:px-2.5 sm:py-2"
      data-cc-card
      style={{
        WebkitTapHighlightColor: 'transparent',
        // Holding a row shouldn't select its text or raise the iOS callout.
        ...(onQuickActions
          ? {
              userSelect: 'none' as const,
              WebkitUserSelect: 'none' as const,
              WebkitTouchCallout: 'none' as const,
            }
          : {}),
      }}
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg sm:mt-1 sm:h-8 sm:w-8 ${iconClassName}`}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        {typeof leadScore === 'number' ? (
          <span className="mb-1 block">
            <LeadScoreThermometer score={leadScore} />
          </span>
        ) : null}
        {/* Line 1 — category label, tinted with the action's accent colour */}
        <span
          className={`block truncate text-[11px] font-semibold leading-tight sm:text-[10px] ${categoryClassName}`}
        >
          {categoryLabel}
        </span>
        {/* Line 2 — title (the entity being acted on) */}
        <span className="flex items-center gap-1.5 min-w-0 mt-0.5">
          <span className="block truncate text-[14px] font-semibold text-gray-900 leading-tight flex-1 min-w-0 sm:text-[13px]">
            {title}
          </span>
          {responseSeverity ? <NewLeadSeverityBadge severity={responseSeverity} /> : null}
        </span>
        {/* Line 3 — customer + status (combined, truncated as a unit) */}
        <span className="block truncate text-[12px] text-gray-500 leading-tight mt-0.5 sm:text-[11px] sm:text-gray-400">
          {meta}
        </span>
      </span>
    </button>
  );
}

function renderActionRow(
  item: CrmRequiredActionItem,
  now: Date,
  onSelectOpportunity: ((id: number) => void) | undefined,
  onSelectProposal: ((id: string) => void) | undefined,
  onSelectPayrollRequest: ((id: string) => void) | undefined,
  onSelectPayrollPeriod: ((start: string, end: string) => void) | undefined,
  onSelectDocumentVerification: ((submissionId: string) => void) | undefined,
  onSelectCampaignBilling: ((campaignId: string) => void) | undefined,
  onSelectMarketingAiTokens: (() => void) | undefined,
  onSelectCallback: ((item: CrmRequiredActionItem) => void) | undefined,
  onSelectDueTask: ((item: CrmRequiredActionItem) => void) | undefined,
  onSelectOrder: ((item: CrmRequiredActionItem) => void) | undefined,
  onLeadQuickActions: ((item: CrmRequiredActionItem, anchor: QuickActionsAnchor) => void) | undefined,
) {
  // Only CRM lead rows get quick actions; they're the ones tied to an opportunity.
  const leadQuickActions =
    onLeadQuickActions && item.opportunityId != null
      ? (anchor: QuickActionsAnchor) => onLeadQuickActions(item, anchor)
      : undefined;
  if (item.kind === 'order_approval') {
    // Mirrors the pinned orders widget row: customer, order number, total, age.
    return (
      <ActionRow
        icon={<ShoppingBag className="h-[18px] w-[18px] sm:h-4 sm:w-4" />}
        iconClassName="bg-blue-50 text-blue-600"
        categoryLabel="Order · Awaiting approval"
        categoryClassName="text-blue-700"
        title={item.title}
        meta={`${item.customerName} · ${item.subtitle}`}
        onClick={() => onSelectOrder?.(item)}
      />
    );
  }

  if (item.kind === 'due_task') {
    const overdue = item.todoOverdue === true;
    return (
      <ActionRow
        icon={<ListTodo className="h-[18px] w-[18px] sm:h-4 sm:w-4" />}
        iconClassName={overdue ? 'bg-red-50 text-red-600' : 'bg-sky-50 text-sky-600'}
        categoryLabel={overdue ? 'Task · Overdue' : 'Task · Due today'}
        categoryClassName={overdue ? 'text-red-700' : 'text-sky-700'}
        title={item.title}
        meta={`${item.customerName} · ${item.subtitle}`}
        onClick={() => onSelectDueTask?.(item)}
      />
    );
  }

  if (item.kind === 'agent_callback') {
    return (
      <ActionRow
        icon={<PhoneCall className="h-[18px] w-[18px] sm:h-4 sm:w-4" />}
        iconClassName="bg-teal-50 text-teal-600"
        categoryLabel="Receptionist · Call back"
        categoryClassName="text-teal-700"
        title={item.title}
        meta={`${item.customerName} · ${item.subtitle}`}
        onClick={() => onSelectCallback?.(item)}
      />
    );
  }

  if (item.kind === 'marketing_ai_tokens_blocked') {
    const paymentFailed = item.title === 'Auto-topup failed';
    return (
      <ActionRow
        icon={<Coins className="h-[18px] w-[18px] sm:h-4 sm:w-4" />}
        iconClassName="bg-amber-50 text-amber-700"
        categoryLabel={
          paymentFailed ? 'Marketing · Auto-topup failed' : 'Marketing · AI tokens low'
        }
        categoryClassName="text-amber-700"
        title={item.title}
        meta={item.subtitle}
        onClick={() => onSelectMarketingAiTokens?.()}
      />
    );
  }

  if (item.kind === 'campaign_billing_failed') {
    return (
      <ActionRow
        icon={<CreditCard className="h-[18px] w-[18px] sm:h-4 sm:w-4" />}
        iconClassName="bg-red-50 text-red-600"
        categoryLabel="Marketing · Payment failed"
        categoryClassName="text-red-700"
        title={item.title}
        meta={item.subtitle}
        onClick={() => {
          if (item.campaignId) onSelectCampaignBilling?.(item.campaignId);
        }}
      />
    );
  }

  if (item.kind === 'document_verification') {
    return (
      <ActionRow
        icon={<ShieldCheck className="h-[18px] w-[18px] sm:h-4 sm:w-4" />}
        iconClassName="bg-indigo-50 text-indigo-600"
        categoryLabel="Document · Sign-off"
        categoryClassName="text-indigo-700"
        title={item.title}
        meta={`${item.customerName} · ${item.subtitle.replace(/^Awaiting sign-off\s*·?\s*/i, '') || 'Awaiting sign-off'}`}
        onClick={() => {
          if (item.documentSubmissionId) onSelectDocumentVerification?.(item.documentSubmissionId);
        }}
      />
    );
  }

  if (item.kind === 'payroll_period') {
    const isOverdue = (item.payrollPeriodDaysPastDue ?? 0) > 0;
    return (
      <ActionRow
        icon={<DollarSign className="h-[18px] w-[18px] sm:h-4 sm:w-4" />}
        iconClassName={isOverdue ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-600'}
        categoryLabel={item.customerName || 'Payroll · Due'}
        categoryClassName={isOverdue ? 'text-amber-700' : 'text-blue-700'}
        title={item.title}
        meta={item.subtitle}
        onClick={() => {
          if (item.payrollPeriodStart && item.payrollPeriodEnd) {
            onSelectPayrollPeriod?.(item.payrollPeriodStart, item.payrollPeriodEnd);
          }
        }}
      />
    );
  }

  if (item.kind === 'payroll_request') {
    // For payroll requests the "title" is the employee name and the customerName field
    // already carries the request type (e.g. "Payroll · Time off"), so we lift that to the
    // category line and drop the customer column on Line 3 (it would just repeat the title).
    return (
      <ActionRow
        icon={<Timer className="h-[18px] w-[18px] sm:h-4 sm:w-4" />}
        iconClassName="bg-violet-50 text-violet-600"
        categoryLabel={item.customerName || 'Payroll · Time request'}
        categoryClassName="text-violet-700"
        title={item.title}
        meta={item.subtitle}
        onClick={() => {
          if (item.payrollRequestId) onSelectPayrollRequest?.(item.payrollRequestId);
        }}
      />
    );
  }

  if (item.kind === 'proposal_convert') {
    // The lib subtitle reads "Approved 3 days ago · convert to booking". The category line
    // already says "Proposal · Approved", so strip both the trailing "· convert to booking"
    // tail and the leading "Approved " — Line 3 just needs the relative time.
    const cleanedSubtitle = item.subtitle
      .replace(/\s*·\s*convert to booking$/i, '')
      .replace(/^approved\s+/i, '');
    const depositLabel =
      item.depositAmount && item.depositAmount > 0
        ? ` · Deposit ${formatCurrency(item.depositAmount)}`
        : '';
    return (
      <ActionRow
        icon={<FileText className="h-[18px] w-[18px] sm:h-4 sm:w-4" />}
        iconClassName="bg-emerald-50 text-emerald-600"
        categoryLabel="Proposal · Approved"
        categoryClassName="text-emerald-700"
        title={item.title}
        meta={`${item.customerName}${depositLabel} · ${cleanedSubtitle}`}
        onClick={() => {
          if (item.proposalId) onSelectProposal?.(item.proposalId);
        }}
      />
    );
  }

  if (item.kind === 'sales_play') {
    return (
      <ActionRow
        icon={<SalesFace size={22} />}
        iconClassName="bg-emerald-50"
        categoryLabel="Lead · Next move"
        categoryClassName="text-emerald-700"
        title={item.title}
        meta={item.subtitle}
        leadScore={item.leadScore}
        onClick={() => {
          if (item.opportunityId != null) onSelectOpportunity?.(item.opportunityId);
        }}
        onQuickActions={leadQuickActions}
      />
    );
  }

  if (item.kind === 'follow_up_due') {
    const dueLabel = item.dueAt
      ? `Due ${formatDistanceToNow(new Date(item.dueAt), { addSuffix: true })}`
      : item.subtitle;
    return (
      <ActionRow
        icon={<Clock className="h-[18px] w-[18px] sm:h-4 sm:w-4" />}
        iconClassName="bg-red-50 text-red-600"
        categoryLabel="Lead · Follow up"
        categoryClassName="text-red-700"
        title={item.title}
        meta={`${item.customerName} · ${dueLabel}`}
        leadScore={item.leadScore}
        onClick={() => {
          if (item.opportunityId != null) onSelectOpportunity?.(item.opportunityId);
        }}
        onQuickActions={leadQuickActions}
      />
    );
  }

  if (item.kind === 'no_activity') {
    const inactiveLabel = item.lastActivityAt
      ? `Last activity ${formatDistanceToNow(new Date(item.lastActivityAt), { addSuffix: true })}`
      : item.subtitle.replace(/^No activity ·\s*/i, '');
    return (
      <ActionRow
        icon={<ZapOff className="h-[18px] w-[18px] sm:h-4 sm:w-4" />}
        iconClassName="bg-orange-50 text-orange-600"
        categoryLabel="Lead · No activity"
        categoryClassName="text-orange-700"
        title={item.title}
        meta={`${item.customerName} · ${inactiveLabel}`}
        leadScore={item.leadScore}
        onClick={() => {
          if (item.opportunityId != null) onSelectOpportunity?.(item.opportunityId);
        }}
        onQuickActions={leadQuickActions}
      />
    );
  }

  // new_lead: drop the redundant "New · " prefix from the time label since the category
  // line already says "Lead · New lead".
  const createdLabel = formatDistanceToNow(new Date(item.createdAt), { addSuffix: true });
  const responseSeverity = getNewLeadResponseSeverity(item.createdAt, now);
  return (
    <ActionRow
      icon={<Sparkles className="h-[18px] w-[18px] sm:h-4 sm:w-4" />}
      iconClassName="bg-blue-50 text-blue-600"
      categoryLabel="Lead · New lead"
      categoryClassName="text-blue-700"
      title={item.title}
      meta={`${item.customerName} · ${createdLabel}`}
      leadScore={item.leadScore}
      responseSeverity={responseSeverity}
      onClick={() => {
        if (item.opportunityId != null) onSelectOpportunity?.(item.opportunityId);
      }}
      onQuickActions={leadQuickActions}
    />
  );
}

function actionRowKey(item: CrmRequiredActionItem): string {
  if (item.kind === 'payroll_period' && item.payrollPeriodStart && item.payrollPeriodEnd) {
    return `payroll-period-${item.payrollPeriodStart}-${item.payrollPeriodEnd}`;
  }
  if (item.kind === 'payroll_request' && item.payrollRequestId) {
    return `payroll-${item.payrollRequestId}`;
  }
  if (item.kind === 'proposal_convert' && item.proposalId) {
    return `proposal-${item.proposalId}`;
  }
  if (item.kind === 'document_verification' && item.documentSubmissionId) {
    return `doc-verify-${item.documentSubmissionId}`;
  }
  if (item.kind === 'campaign_billing_failed' && item.campaignId) {
    return `campaign-billing-${item.campaignId}`;
  }
  if (item.kind === 'marketing_ai_tokens_blocked') {
    return 'marketing-ai-tokens';
  }
  if (item.kind === 'agent_callback' && item.callbackId) {
    return `callback-${item.callbackId}`;
  }
  if (item.kind === 'due_task' && item.todoId) {
    return `due-task-${item.todoId}`;
  }
  if (item.kind === 'order_approval' && item.orderId) {
    return `order-approval-${item.orderId}`;
  }
  if (item.kind === 'sales_play' && item.salesPlayId) {
    return `sales-play-${item.salesPlayId}`;
  }
  return `${item.kind}-${item.opportunityId ?? 'unknown'}`;
}

export default function CrmActionsRequiredPanel({
  actions,
  loading = false,
  variant = 'light',
  onSelectOpportunity,
  onSelectProposal,
  onSelectPayrollRequest,
  onSelectPayrollPeriod,
  onSelectDocumentVerification,
  onSelectCampaignBilling,
  onSelectMarketingAiTokens,
  onSelectCallback,
  onSelectDueTask,
  onSelectOrder,
  onLeadQuickActions,
  fillHeight = false,
  collapsible = false,
  onRequestSummary,
  salesPlays,
  onSalesPlayActed,
  onSalesPlaySettled,
  onSalesPlayFailed,
  className,
}: CrmActionsRequiredPanelProps) {
  const hero = variant === 'hero';
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  const hasItems = actions.total > 0;

  // Mobile dropdown: default open when there are actions, collapsed when none.
  // Once the user taps the header their choice (override) takes precedence.
  const [openOverride, setOpenOverride] = useState<boolean | null>(null);
  const effectiveOpen = openOverride ?? hasItems;
  const handleToggle = () => setOpenOverride(!effectiveOpen);
  // Only items can be expanded/collapsed; an empty bar stays static.
  const showChevron = collapsible && hasItems;
  const collapsedOnMobile = collapsible && hasItems && !effectiveOpen;

  const sortedActions = useMemo(() => flattenRequiredActions(actions), [actions]);
  // A lead the Sales manager has a move staged on renders as one card (lead +
  // draft + Send) in place of the plain lead row.
  const playFor = (item: CrmRequiredActionItem): SalesPlay | null => {
    if (!salesPlays || item.opportunityId == null) return null;
    if (item.kind !== 'new_lead' && item.kind !== 'follow_up_due' && item.kind !== 'no_activity' && item.kind !== 'sales_play') return null;
    return salesPlays.get(item.opportunityId) ?? null;
  };
  const renderItem = (item: CrmRequiredActionItem) => {
    const play = playFor(item);
    if (play) {
      return (
        <SalesPlayCard
          key={play.id}
          play={play}
          item={item}
          now={now}
          onOpenLead={item.opportunityId != null ? () => onSelectOpportunity?.(item.opportunityId!) : undefined}
          onOpenQuote={onSelectProposal}
          onQuickActions={onLeadQuickActions ? (anchor) => onLeadQuickActions(item, anchor) : undefined}
          onActed={(playId, status) => onSalesPlayActed?.(playId, status)}
          onSettled={onSalesPlaySettled}
          onFailed={onSalesPlayFailed}
        />
      );
    }
    return renderActionRow(
      item,
      now,
      onSelectOpportunity,
      onSelectProposal,
      onSelectPayrollRequest,
      onSelectPayrollPeriod,
      onSelectDocumentVerification,
      onSelectCampaignBilling,
      onSelectMarketingAiTokens,
      onSelectCallback,
      onSelectDueTask,
      onSelectOrder,
      onLeadQuickActions,
    );
  };
  // Hero variant groups the inbox into "Overdue" vs "Waiting on you".
  // Mobile keeps one list and filters it; "all" is the default.
  const [attentionFilter, setAttentionFilter] = useState<'all' | 'overdue' | 'waiting'>('all');
  const { overdueItems, waitingItems } = useMemo(() => {
    if (!hero) return { overdueItems: [] as CrmRequiredActionItem[], waitingItems: [] as CrmRequiredActionItem[] };
    const overdue: CrmRequiredActionItem[] = [];
    const waiting: CrmRequiredActionItem[] = [];
    for (const item of sortedActions) {
      (isOverdueItem(item, now) ? overdue : waiting).push(item);
    }
    return { overdueItems: overdue, waitingItems: waiting };
  }, [hero, sortedActions, now]);

  // ── Hero variant: "Needs your attention" as two side-by-side inboxes ──
  if (hero) {
    const renderRows = (items: CrmRequiredActionItem[]) => (
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li key={actionRowKey(item)}>{renderItem(item)}</li>
        ))}
      </ul>
    );

    const inbox = (
      label: string,
      dotClass: string,
      items: CrmRequiredActionItem[],
      emptyCopy: string
    ) => (
      <div className="min-w-0 flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden max-h-[min(440px,55vh)]">
        <div className="flex items-center gap-1.5 px-3 py-2.5 border-b border-gray-100 shrink-0">
          <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${dotClass}`} aria-hidden />
          <h4 className="text-[13px] font-semibold text-gray-900 truncate">{label}</h4>
          <span
            className={`ml-auto text-[11px] font-semibold tabular-nums px-1.5 py-0.5 rounded-full ${
              items.length > 0 ? 'bg-gray-100 text-gray-700' : 'bg-gray-50 text-gray-400'
            }`}
          >
            {items.length}
          </span>
        </div>
        <div
          className="flex-1 min-h-0 overflow-y-auto ios-momentum-scroll p-1.5"
          // Chain to page scroll once the list hits its end (overrides the
          // global `.ios-momentum-scroll { overscroll-behavior: contain }`),
          // so scrolling "through" an inbox continues down the page.
          style={{ overscrollBehaviorY: 'auto' }}
        >
          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-4 w-4 animate-spin text-gray-300" aria-hidden />
            </div>
          ) : items.length === 0 ? (
            <p className="py-8 px-3 text-center text-[12px] text-gray-400">{emptyCopy}</p>
          ) : (
            renderRows(items)
          )}
        </div>
      </div>
    );

    return (
      <section
        className={`relative z-0 flex flex-col min-h-0 ${className ?? ''}`}
        data-crm-actions-required
        aria-label="Actions required"
      >
        <div className="flex items-center gap-2 mb-2 px-0.5">
          <h3 className="text-[15px] font-semibold text-gray-900 leading-tight">Needs your attention</h3>
          {!loading ? (
            <span
              className={`text-[12px] font-semibold tabular-nums px-2 py-0.5 rounded-full ${
                hasItems ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'
              }`}
            >
              {actions.total}
            </span>
          ) : (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-hidden />
          )}
          {onRequestSummary ? (
            <button
              type="button"
              onClick={onRequestSummary}
              title="AI summary of your pipeline"
              aria-label="AI summary of your pipeline"
              className="ml-auto shrink-0 inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-[12px] font-semibold text-blue-700 transition-colors hover:bg-blue-100 active:scale-95"
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
              <span>Summary</span>
            </button>
          ) : null}
        </div>
        {(() => {
          const showOverdue = overdueItems.length > 0;
          const showWaiting = waitingItems.length > 0;
          if (!showOverdue && !showWaiting) {
            return (
              <div className="rounded-xl border border-gray-200 bg-white shadow-sm py-10 px-4 text-center">
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-gray-300 mx-auto" aria-hidden />
                ) : (
                  <>
                    <p className="text-sm font-medium text-gray-600">You&apos;re all caught up</p>
                    <p className="text-xs mt-1 text-gray-400">Nothing needs your attention right now</p>
                  </>
                )}
              </div>
            );
          }
          // An empty inbox is hidden and the other takes the full width; when
          // both show, grid stretch keeps the two boxes the same height.
          // Phones use one list with filter tags instead of two columns.
          const mobileItems = sortRequiredActionsNewestFirst(
            attentionFilter === 'overdue'
              ? overdueItems
              : attentionFilter === 'waiting'
                ? waitingItems
                : [...overdueItems, ...waitingItems],
          );
          const mobileEmpty =
            attentionFilter === 'overdue'
              ? 'Nothing overdue'
              : attentionFilter === 'waiting'
                ? 'Nothing waiting on you'
                : 'Nothing needs your attention right now';
          const filterTag = (
            key: 'overdue' | 'waiting',
            label: string,
            count: number,
            selectedClass: string,
            dotClass: string,
          ) => {
            const selected = attentionFilter === key;
            return (
              <button
                type="button"
                onClick={() => setAttentionFilter((current) => (current === key ? 'all' : key))}
                aria-pressed={selected}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-semibold transition-colors touch-manipulation ${
                  selected ? selectedClass : 'border-gray-200 bg-white text-gray-600'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${dotClass}`} aria-hidden />
                {label}
                <span className="tabular-nums">{count}</span>
              </button>
            );
          };
          return (
            <>
              <div className="md:hidden flex flex-col min-h-0 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden max-h-[min(440px,55vh)]">
                <div className="flex items-center gap-1.5 px-3 py-2.5 border-b border-gray-100 shrink-0">
                  {filterTag('overdue', 'Overdue', overdueItems.length, 'border-red-200 bg-red-50 text-red-800', 'bg-red-500')}
                  {filterTag('waiting', 'Waiting on you', waitingItems.length, 'border-amber-200 bg-amber-50 text-amber-900', 'bg-amber-500')}
                </div>
                <div
                  className="flex-1 min-h-0 overflow-y-auto ios-momentum-scroll p-1.5"
                  style={{ overscrollBehaviorY: 'auto' }}
                >
                  {loading && mobileItems.length === 0 ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-4 w-4 animate-spin text-gray-300" aria-hidden />
                    </div>
                  ) : mobileItems.length === 0 ? (
                    <p className="py-8 px-3 text-center text-[12px] text-gray-400">{mobileEmpty}</p>
                  ) : (
                    renderRows(mobileItems)
                  )}
                </div>
              </div>
              <div className={`hidden md:grid gap-3 ${showOverdue && showWaiting ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {showOverdue ? inbox('Overdue', 'bg-red-500', overdueItems, 'Nothing overdue') : null}
                {showWaiting ? inbox('Waiting on you', 'bg-amber-500', waitingItems, 'Nothing waiting on you') : null}
              </div>
            </>
          );
        })()}
      </section>
    );
  }

  return (
    <aside
      className={`relative z-0 flex flex-col overflow-hidden max-lg:rounded-xl ${
        fillHeight
          ? 'h-full min-h-0 max-h-full flex-1 max-lg:h-auto max-lg:flex-none max-lg:max-h-none'
          : 'h-[min(480px,60vh)] max-lg:h-auto'
      } ${className ?? ''}`}
      data-cc-inset
      data-crm-actions-required
      aria-label="Actions required"
    >
      <header
        className="flex-shrink-0 px-3 py-2.5 sm:px-4 sm:py-3"
        data-cc-inset-header
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={collapsible ? handleToggle : undefined}
            aria-expanded={showChevron ? effectiveOpen : undefined}
            className="flex min-w-0 flex-1 items-center justify-between gap-2 text-left lg:pointer-events-none"
          >
            <h3 className="text-[15px] font-semibold text-gray-900 leading-tight sm:text-sm">
              Actions Required
            </h3>
            <span className="flex items-center gap-2 shrink-0">
              {!loading ? (
                <span
                  className={`text-[12px] font-semibold tabular-nums px-2 py-0.5 rounded-full sm:text-[11px] ${
                    hasItems ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {actions.total}
                </span>
              ) : (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400 sm:hidden" aria-hidden />
              )}
              {showChevron ? (
                <ChevronDown
                  className={`h-4 w-4 text-gray-400 transition-transform lg:hidden ${effectiveOpen ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              ) : null}
            </span>
          </button>
          {onRequestSummary ? (
            <button
              type="button"
              onClick={onRequestSummary}
              title="AI summary of your pipeline"
              aria-label="AI summary of your pipeline"
              className="shrink-0 inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-[12px] font-semibold text-blue-700 transition-colors hover:bg-blue-100 active:scale-95 sm:text-[11px]"
            >
              <Sparkles className="h-4 w-4 sm:h-3.5 sm:w-3.5" aria-hidden />
              <span>Summary</span>
            </button>
          ) : null}
        </div>
        <p className="hidden sm:block text-[11px] text-gray-500 mt-1">
          Newest first — tasks, leads, payroll, and more
        </p>
        {!loading && !hasItems ? (
          <p className="sm:hidden text-[12px] text-gray-500 leading-tight mt-1">All caught up</p>
        ) : null}
      </header>

      <div
        className={
          hasItems
            ? `flex-1 min-h-0 overflow-y-auto ios-momentum-scroll max-lg:max-h-[20rem] max-lg:flex-none px-2 pb-2 pt-0 sm:p-3 ${
                collapsedOnMobile ? 'max-lg:hidden' : ''
              }`
            : 'flex-1 min-h-0 max-lg:hidden p-2 sm:p-3'
        }
        // Allow the page to keep scrolling once this list reaches its end
        // (override the global `.ios-momentum-scroll { overscroll-behavior: contain }`).
        style={hasItems ? { overscrollBehaviorY: 'auto' } : undefined}
      >
        {loading ? (
          <>
            <div className="hidden sm:flex flex-col items-center justify-center py-12 gap-2 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              <span className="text-xs">Loading…</span>
            </div>
          </>
        ) : !hasItems ? (
          <div className="hidden sm:flex flex-col items-center justify-center py-12 px-4 text-center">
            <p className="text-sm font-medium text-gray-600">You&apos;re all caught up</p>
            <p className="text-xs mt-1 text-gray-400">Nothing needs your attention right now</p>
          </div>
        ) : (
          <ul className="space-y-1.5 sm:space-y-2">
            {sortedActions.map((item) => (
              <li key={actionRowKey(item)}>{renderItem(item)}</li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}
